/* eslint-disable @typescript-eslint/no-explicit-any */
import { Input } from "@/shared/components/ui/input"
import { Textarea } from "@/shared/components/ui/textarea"
import { Switch } from "@/shared/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select"
import { FormField, FormItem, FormLabel, FormDescription, FormMessage, FormControl } from "@/shared/components/ui/form"
import { BaseFieldProps } from "./BaseField"

/**
 * Normaliza o valor para exibição em <Textarea>. Sem isso, qualquer objeto
 * passa pelo coerce do React e vira a string "[object Object]" no DOM.
 *   - null/undefined → ''
 *   - objeto vazio    → ''
 *   - objeto preenchido → JSON.stringify formatado
 *   - demais          → o valor como veio
 */
function formatTextareaValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    return Object.keys(value as object).length === 0
      ? ''
      : JSON.stringify(value, null, 2);
  }
  return value as string;
}

export function TextField({ field, name, control }: BaseFieldProps) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field: formField }) => {
        // Handle case where value is an empty object
        const value = formField.value && typeof formField.value === 'object' && 
                     Object.keys(formField.value).length === 0 ? 
                     '' : formField.value;
        
        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              <Input
                {...formField}
                value={value}
                placeholder={field.placeholder}
                type={field.type === 'file' ? 'file' : 'text'}
              />
            </FormControl>
            {field.description && <FormDescription>{field.description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  )
}

export function TextAreaField({ field, name, control }: BaseFieldProps) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field: formField }) => {
        const value = formatTextareaValue(formField.value);

        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              <Textarea
                {...formField}
                value={value}
                placeholder={field.placeholder}
                className="min-h-[100px]"
              />
            </FormControl>
            {field.description && <FormDescription>{field.description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  )
}

export function NumberField({ field, name, control }: BaseFieldProps) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>{field.label}</FormLabel>
          <FormControl>
            <Input
              {...formField}
              type="number"
              onChange={e => formField.onChange(Number(e.target.value))}
            />
          </FormControl>
          {field.description && <FormDescription>{field.description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function BooleanField({ field, name, control }: BaseFieldProps) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field: formField }) => (
        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
          <div className="space-y-0.5">
            <FormLabel>{field.label}</FormLabel>
            {field.description && (
              <FormDescription className="text-xs">{field.description}</FormDescription>
            )}
          </div>
          <FormControl>
            <Switch
              checked={formField.value}
              onCheckedChange={formField.onChange}
            />
          </FormControl>
        </FormItem>
      )}
    />
  )
}

export function SelectField({ field, name, control }: BaseFieldProps) {
  // Tratar valores especiais e placeholders
  const placeholder = field.placeholder || '';

  const modifiedOptions = field.options?.map((option: any) => {
    if (typeof option === 'object') {
      return option.value === '' ? { ...option, value: '__EMPTY__', originalValue: '' } : option;
    }
    return option === '' ? { value: '__EMPTY__', label: '', originalValue: '' } : option;
  });

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>{field.label}</FormLabel>
          {field.description && <FormDescription>{field.description}</FormDescription>}
          <Select
            onValueChange={(value) => {
              const finalValue = value === '__EMPTY__' ? '' : value;
              formField.onChange(finalValue);
            }}
            value={(() => {
              const raw = formField.value;
              const hasEmptyOption = Array.isArray(modifiedOptions)
                ? modifiedOptions.some((opt: any) => (typeof opt === 'object' ? opt.value : opt) === '__EMPTY__')
                : false;

              const isPrimitive = typeof raw === 'string' || typeof raw === 'number';

              if (raw === '' || raw === null || raw === undefined || !isPrimitive) {
                return hasEmptyOption ? '__EMPTY__' : undefined;
              }
              return String(raw);
            })()}
          >
            <FormControl>
              <SelectTrigger>
                {/* Usar apenas o placeholder padrão do SelectValue para evitar render de objetos */}
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {modifiedOptions?.map((option: any) => {
                const value = typeof option === 'object' ? option.value : option;
                const label = typeof option === 'object' ? option.label : option;
                return (
                  <SelectItem key={value} value={String(value)}>
                    {label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function JsonField({ field, name, control }: BaseFieldProps) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field: formField }) => {
        const value = formatTextareaValue(formField.value);

        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              <Textarea
                {...formField}
                value={value}
                placeholder={field.placeholder || '{\n  \n}'}
                className="min-h-[200px] font-mono"
              />
            </FormControl>
            {field.description && <FormDescription>{field.description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  )
}

export { EmailTemplateSelectorField } from './EmailTemplateSelectorField';

export function ObjectField({ field, name, control: _control, renderField }: BaseFieldProps & { renderField: any }) {
  if (!Array.isArray(field.fields)) {
    return null;
  }

  return (
    <div className="space-y-4 border rounded-lg p-4 mt-4">
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium">{field.label}</h4>
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {field.fields.map((subField: any) => (
          <div key={`${name}.${subField.name}`}>
            {renderField(subField.name, subField)}
          </div>
        ))}
      </div>
    </div>
  )
}
